import { Box, Card, Stack, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { ConnectedApp } from "@/api/oauth";
import { RouteButton } from "@/components/common/styles";
import useAnchorScroll from "@/hooks/useAnchorScroll";
import useSWR from "@/hooks/useSWR";
import { PostHogEvents, useEvents } from "@/tracking/useEvents";
import AiAgentsVisual from "./AiAgentsVisual";

/**
 * Deep-link target for email campaigns: `/dashboard#ai-agents`, scrolled into
 * view by `useAnchorScroll`. Kept from the section this banner replaced, so
 * the links already sent still land here.
 */
const AI_AGENTS_ANCHOR_ID = "ai-agents";

type BannerState = "default" | "connected";

/** The `button` property of the click event, one per button. */
type BannerButton =
  | "connect_mcp_server"
  | "manage_connected_apps"
  | "browse_skills";

type BannerLink = {
  button: BannerButton;
  label: string;
  to:
    | "/integrations/mcp"
    | "/integrations/skills"
    | "/integrations/connected-apps";
  /** `false` keeps the router from running the destination's loader ahead of a click. */
  preload?: false;
};

const BROWSE_SKILLS: BannerLink = {
  button: "browse_skills",
  label: "Browse Skills",
  to: "/integrations/skills",
  // The router preloads every route whose link is in the viewport, and the
  // skills loader fetches the catalog from GitHub, which the page's
  // `connect-src` does not allow (see `integrations/skills.tsx`): preloading
  // it from the dashboard only logs a CSP violation on every visit and serves
  // the snapshot anyway. The click runs the same loader, as the nav's link does.
  preload: false,
};

/**
 * What the banner says in each state. Connected needs at least one
 * application; the title counts them, the subtitle never changes, and neither
 * names an application.
 */
function bannerContent(apps: ConnectedApp[] | undefined): {
  state: BannerState;
  title: string;
  subtitle: string;
  links: BannerLink[];
} {
  if (!apps?.length) {
    return {
      state: "default",
      title: "Use Webshare with your AI agent",
      subtitle:
        "Connect your AI client to manage proxies, usage and billing, or install a ready-made skill.",
      links: [
        {
          button: "connect_mcp_server",
          label: "Connect MCP Server",
          to: "/integrations/mcp",
        },
        BROWSE_SKILLS,
      ],
    };
  }
  return {
    state: "connected",
    title:
      apps.length === 1
        ? "Your AI agent is connected"
        : "Your AI agents are connected",
    subtitle:
      "Change what they can access, or disconnect them, in Connected Apps.",
    links: [
      {
        button: "manage_connected_apps",
        label: "Manage Connected Apps",
        to: "/integrations/connected-apps",
      },
      BROWSE_SKILLS,
    ],
  };
}

/**
 * The dashboard's AI agent banner (r-2140): the default state invites the
 * reader to connect an MCP client, the connected state shows the clients that
 * have. Shown to every account on every plan, and not dismissible.
 *
 * The visual sits beside the copy from `lg` up and above it below that, as
 * the design's Desktop and M/SM frames have it: at `md` the drawer leaves the
 * content narrower than a tablet. The buttons stack on a phone.
 */
export default function DashboardAiAgentBanner() {
  // The key the Connected Apps page reads, so SWR makes one request for both.
  // While it loads, or if it fails (a sub-user's 403, say), the banner shows
  // the default state.
  const { data: apps } = useSWR<ConnectedApp[]>("/oauth/connected-apps/");
  const { captureEvent } = useEvents();

  useAnchorScroll(AI_AGENTS_ANCHOR_ID);

  const content = bannerContent(apps);

  const trackClick = (button: BannerButton) =>
    captureEvent(PostHogEvents.DashboardAiBannerClicked, {
      state: content.state,
      button,
    });

  return (
    <Box
      id={AI_AGENTS_ANCHOR_ID}
      sx={(theme) => ({ p: 3, pt: 0, scrollMarginTop: theme.spacing(8) })}
    >
      <Card
        data-testid="ai-agent-banner"
        sx={{
          p: 3,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          gap: 3,
        }}
      >
        <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={{ xs: 1, lg: 0 }}>
            <Typography variant="h6">{content.title}</Typography>
            <Typography variant="body1" color="text.secondary">
              {content.subtitle}
            </Typography>
          </Stack>
          {/* On a phone the buttons stack and fill the width (the SM frame);
              from `sm` they sit in a row, wrapping if the card is too narrow
              for both. */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "flex-start" }}
            spacing={2}
            useFlexGap
            flexWrap="wrap"
          >
            {content.links.map((link) => (
              <RouteButton
                key={link.button}
                to={link.to}
                preload={link.preload}
                variant="outlined"
                color="neutral"
                endIcon={<ChevronRightIcon />}
                onClick={() => trackClick(link.button)}
                data-testid={`ai-agent-banner-${link.button}`}
              >
                {link.label}
              </RouteButton>
            ))}
          </Stack>
        </Stack>
        <Box sx={{ order: { xs: -1, lg: 0 } }}>
          <AiAgentsVisual apps={apps} />
        </Box>
      </Card>
    </Box>
  );
}
